/**
 * 顧客詳細ページ。Phase A / P-C で「モーダル → ページ遷移」化。
 *
 * URL: /sales/contacts/:contactId
 * 構成:
 *   ヘッダー: パンくず + 顧客名 / 会社 / kind バッジ / promotionLevel / メール
 *   タブ: 基本情報 / 商談 / 所有商品
 *
 * 2026-10-30: 開いているタブを URL (?tab=) に持たせる。商談詳細の「前の画面へ」/ ブラウザの戻るで
 *   戻った時やリロード時に、同じタブで開くため。
 */
import { resolveRetentionState } from '@goenhub/shared';
import {
  ArrowLeft,
  Building2,
  CalendarClock,
  Flag,
  Mail,
  UserCircle2,
  Cpu,
  Briefcase,
  History,
  IdCard,
  Users,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMailContact } from '@/hooks/useMailContacts';
import { useUrlEnumParam } from '@/hooks/useUrlParam';
import { toDate } from '@/lib/firestoreTime';
import { paths } from '@/routes/paths';

import { ContactBasicTab } from './tabs/ContactBasicTab';
import { ContactChangesTab } from './tabs/ContactChangesTab';
import { ContactDealsTab } from './tabs/ContactDealsTab';
import { ContactDevicesTab } from './tabs/ContactDevicesTab';
import { ContactGermanyReportTab } from './tabs/ContactGermanyReportTab';

const CONTACT_TABS = ['basic', 'deals', 'devices', 'germany', 'changes'] as const;

export function SalesContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const { data: contact, isLoading, error } = useMailContact(contactId);
  const [tab, setTab] = useUrlEnumParam('tab', CONTACT_TABS, 'basic');

  if (!contactId) {
    return (
      <Screen title="顧客詳細">
        <div className="text-sm text-[var(--accent-red)]">
          contactId が指定されていません。
        </div>
      </Screen>
    );
  }

  if (isLoading) {
    return (
      <Screen title="顧客詳細">
        <Spinner />
      </Screen>
    );
  }

  if (error || !contact) {
    return (
      <Screen title="顧客詳細">
        <div className="space-y-2">
          <p className="text-sm text-[var(--accent-red)]">
            顧客が見つかりませんでした。
          </p>
          <Link
            to={paths.salesContacts}
            className="text-xs text-[var(--accent)] hover:underline inline-flex items-center gap-1"
          >
            <ArrowLeft className="size-3" />
            顧客リストに戻る
          </Link>
        </div>
      </Screen>
    );
  }

  const isBtoB = contact.kind === 'btob';
  const promotion = contact.promotionLevel ?? 'lead';
  const retentionUntil = toDate(contact.saRetentionUntil);
  const retention = resolveRetentionState({
    hasOwnerSa: !!contact.ownerSaUid,
    until: retentionUntil,
    now: new Date(),
  });

  return (
    <Screen title={contact.name || '(無名)'} restoreScroll>
      {/* パンくず */}
      <div className="mb-3">
        <Link
          to={paths.salesContacts}
          className="text-xs text-[var(--text-muted)] hover:text-[var(--accent)] inline-flex items-center gap-1"
        >
          <ArrowLeft className="size-3" />
          顧客リストに戻る
        </Link>
      </div>

      {/* ヘッダーカード */}
      <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {isBtoB ? (
                <Building2 className="size-4 text-[var(--accent)]" />
              ) : (
                <UserCircle2 className="size-4 text-[var(--accent-purple)]" />
              )}
              <h1 className="text-lg font-semibold text-[var(--text-primary)]">
                {contact.name || '(無名)'}
              </h1>
              {contact.kind && (
                <Badge variant="default">
                  {contact.kind === 'btob'
                    ? 'BtoB'
                    : contact.kind === 'btoc'
                      ? 'BtoC'
                      : 'Unknown'}
                </Badge>
              )}
              {promotion === 'inactive' ? (
                <Badge variant="default">休眠</Badge>
              ) : promotion === 'customer' ? (
                <Badge variant="approved">顧客</Badge>
              ) : (
                <Badge variant="draft">リード</Badge>
              )}
              {contact.isTestContact === true && (
                <Badge variant="danger" title="テスト顧客: 商談の自動マージ対象外">
                  テスト顧客
                </Badge>
              )}
              {contact.ownerSaUid && (
                <>
                  <Badge variant="review" title="担当SA (外部営業)">
                    <Users className="size-3" />
                    SA: {contact.ownerSaName || contact.ownerSaUid}
                  </Badge>
                  {retentionUntil && (
                    <Badge
                      variant={
                        retention.state === 'expiring' ? 'draft' : 'default'
                      }
                      title="SA 顧客保有期限。この日を過ぎると担当から外れます"
                    >
                      <CalendarClock className="size-3" />
                      顧客期限 {fmtDate(contact.saRetentionUntil)}
                      {retention.daysLeft !== null && retention.daysLeft >= 0
                        ? ` (あと${retention.daysLeft}日)`
                        : ''}
                    </Badge>
                  )}
                </>
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-[var(--text-secondary)]">
              {contact.companyName && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <Briefcase className="size-3 text-[var(--text-muted)] flex-shrink-0" />
                  <span className="truncate">
                    {contact.companyName}
                    {contact.department ? ` / ${contact.department}` : ''}
                    {contact.jobTitle ? ` ${contact.jobTitle}` : ''}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1.5 min-w-0">
                <Mail className="size-3 text-[var(--text-muted)] flex-shrink-0" />
                <span className="truncate">{contact.defaultEmail || '—'}</span>
              </div>
              {contact.customerSince && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <IdCard className="size-3 text-[var(--text-muted)] flex-shrink-0" />
                  <span>初契約: {fmtDate(contact.customerSince)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* タブ */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as (typeof CONTACT_TABS)[number])}>
        <TabsList>
          <TabsTrigger value="basic">基本情報</TabsTrigger>
          <TabsTrigger value="deals">
            <Briefcase className="size-3.5 mr-1" />
            商談
          </TabsTrigger>
          <TabsTrigger value="devices">
            <Cpu className="size-3.5 mr-1" />
            所有商品
          </TabsTrigger>
          <TabsTrigger value="germany">
            <Flag className="size-3.5 mr-1" />
            ドイツ報告
          </TabsTrigger>
          <TabsTrigger value="changes">
            <History className="size-3.5 mr-1" />
            変更履歴
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basic">
          <ContactBasicTab contactId={contactId} contact={contact} />
        </TabsContent>
        <TabsContent value="deals">
          <ContactDealsTab contactId={contactId} />
        </TabsContent>
        <TabsContent value="devices">
          <ContactDevicesTab contactId={contactId} />
        </TabsContent>
        <TabsContent value="germany">
          <ContactGermanyReportTab contactId={contactId} contact={contact} />
        </TabsContent>
        <TabsContent value="changes">
          <ContactChangesTab contactId={contactId} />
        </TabsContent>
      </Tabs>
    </Screen>
  );
}

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}
